import type { OwnIcon } from "../shared/icons";
import { LABEL_PROBLEM, labelOf, QUESTION_LIMITS } from "../shared/questions";
import { noop } from "../shared/quietly";
import { type CopyRow, RecordCopy, refused, type WriteKind } from "../shared/views/copy";
import { collectionSpec, type Manifest, normalize, readManifest, sectionFor } from "../shared/views/manifest";
import { POST_LIMITS } from "../shared/views/posts";
import { answerLocally, type Query } from "../shared/views/query";
import {
  applyOp,
  type Fields,
  type Op,
  type Patch,
  RECORD_ID,
  type Rec,
  type UsedCollection,
} from "../shared/views/records";
import { type Place, parsePartition, placeWriter, type Writer } from "../shared/views/rules";
import type { SeedWrite } from "../shared/views/seed";
import { type HistoryEntry, stageOps } from "../shared/views/stage";
import type { DataAck, DataInfo, PartCopy, ViewMember } from "./connection";
import type { DataChange, FrameData } from "./frame/protocol";

/**
 * What the data host needs from a view's connection: its copies of the partitions, what the view's object says about
 * this connection, and a way to write. It dispatches `data` when a partition changes or becomes ready, and `data-info`
 * when the object describes the connection.
 */
export interface DataConnection extends EventTarget {
  readonly parts: Map<string, PartCopy>;
  readonly data: DataInfo | null;
  readonly readyParts: Set<string>;
  /** A partition's records of a collection as this device sees them, its own unanswered writes included. */
  rows(part: string, collection: string): Rec[];
  collections(part: string): string[];
  writeData(part: string, op: Op): Promise<DataAck>;
  history(part: string, collection: string, record: string): Promise<unknown[]>;
  query(name: string, params: Record<string, unknown>, keep?: boolean): Promise<unknown>;
  /** One of the view's actions, which its object runs on the server as this connection's person. */
  call(name: string, params: Record<string, unknown>, chat: string | null): Promise<unknown>;
  /** An update or an alert from the page, which the view's object posts to the chat as this connection's person via the view. */
  post(post: PagePost): Promise<unknown>;
  /**
   * A question's view's answer, which the view's object keeps as the answer of this connection's person, the one asked,
   * with the label of what they chose if the page gave one.
   */
  respond(value: unknown, label?: string): Promise<unknown>;
  cached?(name: string, params: Record<string, unknown>): { value: unknown; at: string } | null;
  answers?(): Record<string, { value: unknown; at: string }>;
}
/**
 * What a connection's `data` event says changed: records of one partition, by collection and id; the whole partition
 * when `records` is absent; everything when `part` is too; or only the views it reads through `uses`.
 */
export interface DataChangeDetail {
  part?: string;
  records?: { collection: string; id: string }[];
  uses?: true;
}
export type Person = {
  id: string;
  name: string;
  short: string;
  color: string;
  kind: "person";
  role: string | null;
  title?: string;
  status: string | null;
  here: boolean;
  /** Their icon in Coast, an emoji or a picture, which `kit.av` draws as the Coast app does; the renderer gives a
   * picture its full address. */
  icon: OwnIcon | null;
  gone?: true;
};

/** A steady color for someone the server has not given one yet. */
const colorOf = (name: string) =>
  `hsl(${[...name].reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) % 360, 7)} 45% 55%)`;
const newId = () => crypto.randomUUID().replace(/-/g, "").slice(0, 12);
/** The operation a write of the page API is, as the view's object takes it. */
const opOf = (kind: WriteKind, collection: string, id: string, value?: Fields | Patch): Op =>
  kind === "remove"
    ? { op: "remove", collection, id }
    : kind === "update"
      ? { op: "update", collection, id, patch: (value ?? {}) as Patch }
      : { op: kind, collection, id, fields: (value ?? {}) as Fields };

/**
 * A post from the page, checked (docs/views.md#6-the-page-api): an update in the chat's timeline, grouped by its
 * `topic` and `summary`, or an alert, which mentions people and notifies them; as the person who acted, via the view.
 */
export interface PagePost {
  topic?: string;
  alert: boolean;
  text: string;
  /** `#<section>?<params>` for this view's, or a link to share another view's in the same chat (`frame/store.ts`). */
  link: string | null;
  mention: string[];
  /** The chat it posts to: the view's own in a chat, and one of those it reads in its home. */
  chat: string | null;
  summary?: string;
}

/**
 * The runtime's side of `coast.data` (docs/views.md#6-the-page-api): reads from the connection's copies of the
 * partitions it opened, writes through the connection so the page hears the view's object answer, and `can` with the
 * same rules the server applies (shared/views/rules.ts), so a control a rule would refuse is not offered. A page reads
 * only the collections its manifest's scopes put in the partitions this connection has; another view's collections,
 * from `uses`, come from the object, read-only.
 */
export class DataHost {
  private manifestCache?: { source: string; manifest: Manifest };
  /** Called when the data a page reads changes, and when the view's object says who this connection is. */
  onChange: (change: { part?: string }) => void = noop;
  onInfo: () => void = noop;
  /** Counts the changes to the data, so a page's frame built before one is sent everything again (frame/protocol.ts). */
  version = 0;
  /** The records as the page reads them, from the connection's copies (shared/views/copy.ts), as the frame reads its own. */
  private readonly copy = new RecordCopy({
    place: () => this.place(),
    rules: (collection) => collectionSpec(this.manifest(), collection),
    parts: () => (this.connection.data?.parts ?? []).filter((key) => this.connection.parts.has(key)),
    collections: (part) => this.connection.collections(part),
    rows: (part, collection) => {
      const partition = parsePartition(part);
      const chat = partition?.scope === "chat" ? partition.chat : null;
      return this.connection.rows(part, collection).map((rec) => ({ ...rec, chat }));
    },
    used: (collection) => this.used(collection),
    section: (collection) => sectionFor(this.manifest(), collection),
  });
  /** What changed since the page's frame was last told: records by partition and collection, or whole partitions. */
  private dirty = new Map<string, Map<string, Set<string>> | "whole">();
  private dirtyAll = false;
  private dirtyUses = false;
  /** The frame's copy as this host last told it: each collection's record ids in their order, by `part\ncollection`. */
  private told = new Map<string, string[]>();
  private toldReady = false;
  constructor(
    private readonly connection: DataConnection,
    private readonly source: () => string,
  ) {
    connection.addEventListener("data", (event) => {
      const detail: DataChangeDetail = (event as CustomEvent).detail ?? {};
      this.note(detail);
      this.version++;
      this.onChange(detail);
    });
    connection.addEventListener("data-info", () => this.onInfo());
  }

  manifest(): Manifest {
    const source = this.source();
    if (this.manifestCache?.source === source) return this.manifestCache.manifest;
    let manifest: Manifest;
    try {
      manifest = readManifest(source);
    } catch {
      manifest = normalize({ name: "View" });
    }
    this.manifestCache = { source, manifest };
    return manifest;
  }
  get info(): DataInfo | null {
    return this.connection.data;
  }
  /** Whether every partition's data is here, from the view's object or from this device. */
  ready(): boolean {
    const info = this.connection.data;
    return !!info && info.parts.every((part) => this.connection.readyParts.has(part));
  }

  /** Where this connection's data is: its chat, or in the view's home the chats it reads (docs/views.md#1-views). */
  private place(): Place | null {
    const info = this.connection.data;
    return info ? { chats: info.chats ?? (info.chat ? [info.chat] : []), writer: info.writer } : null;
  }
  find(collection: string, query: Query = {}): CopyRow[] {
    return this.copy.find(collection, query);
  }
  get(collection: string, id: string): CopyRow | undefined {
    return this.copy.get(collection, id);
  }

  /** A write: shown here at once, and resolved with the record once the view's object has answered, or refused. */
  async write(
    collection: string,
    kind: WriteKind,
    id: string | null,
    value?: Fields | Patch,
    options: { chat?: string } = {},
  ): Promise<Rec | undefined> {
    const recordId = id ?? newId();
    if (!RECORD_ID.test(recordId)) throw Object.assign(new Error("Invalid record id."), { code: "invalid" });
    const plan = this.copy.plan(collection, kind, recordId, options);
    if (refused(plan)) throw Object.assign(new Error(plan.error), { code: plan.code });
    const op = opOf(kind, collection, recordId, value);
    // What can't apply is refused here, before it goes anywhere.
    try {
      applyOp(plan.before, op);
    } catch (error) {
      throw Object.assign(new Error(error instanceof Error ? error.message : "Invalid write."), { code: "invalid" });
    }
    const ack = await this.connection.writeData(plan.part, op);
    if (ack.refused.some((entry) => entry.collection === collection && entry.id === recordId))
      throw Object.assign(new Error(`The view refused that change to ${collection}.`), { code: "forbidden" });
    return kind === "remove" ? undefined : this.get(collection, recordId);
  }

  /** Whether a write would be allowed. In the home, an insert that names no chat asks whether any of its chats would take it. */
  can(
    collection: string,
    action: string,
    record: (Rec & { chat?: string | null }) | null,
    options: { chat?: string } = {},
  ): boolean {
    return this.copy.can(collection, action, record, options);
  }

  history(collection: string, id: string): Promise<unknown[]> {
    const part = this.copy.partOf(collection, this.get(collection, id)?.chat);
    return part ? this.connection.history(part, collection, id) : Promise.resolve([]);
  }

  /**
   * One of the view's declared queries as the viewer: one over the view's records from this device's copy, as the
   * server would answer it (`answerLocally`); one through a connector run by the view's object.
   */
  query(name: string, params: Record<string, unknown>): Promise<unknown> {
    const queries = this.manifest().queries;
    const local = answerLocally(queries, name, params, this.connection.data?.writer.id ?? null, (collection, query) =>
      this.find(collection, query),
    );
    if (local)
      return "error" in local
        ? Promise.reject(Object.assign(new Error(local.error), { code: "query" }))
        : Promise.resolve(local.value);
    return this.connection.query(name, params, queries[name]!.cache !== false);
  }
  /**
   * `coast.post`: an update, or an alert, checked here, then posted by the view's object (`PagePost`). `kind` is
   * `"update"` or `"alert"`, with an update's `topic` beside the text, or the topic itself, such as `issue.assigned`,
   * with `alert: true` for an alert: the two shapes an action's `coast.post` takes too.
   */
  post(kind: unknown, item: unknown): Promise<unknown> {
    const value = (item && typeof item === "object" ? item : {}) as Record<string, unknown>;
    const fail = (message: string) => Promise.reject(Object.assign(new Error(message), { code: "invalid" }));
    const plain = kind === "update" || kind === "alert";
    const topic = plain ? value.topic : kind;
    if (topic !== undefined && (typeof topic !== "string" || !/^[a-z0-9][a-z0-9._-]{0,63}$/.test(topic)))
      return fail(
        "A post's kind is update, alert, or a topic: a short name such as issue.assigned, in lowercase letters, digits, dots, - or _.",
      );
    const text = typeof value.text === "string" ? value.text.trim() : "";
    if (!text || text.length > POST_LIMITS.text) return fail(`A post's text is 1 to ${POST_LIMITS.text} characters.`);
    const mention = value.mention === undefined || value.mention === null ? [] : [value.mention].flat();
    if (mention.length > POST_LIMITS.mention || mention.some((id) => typeof id !== "string"))
      return fail(`A post mentions up to ${POST_LIMITS.mention} people, by their ids.`);
    const alert = kind === "alert" || value.alert === true;
    if (mention.length && !alert)
      return fail(
        "An update notifies no one, so it mentions no one: post an alert, with alert: true, to reach people.",
      );
    const summary = typeof value.summary === "string" ? value.summary.trim() : undefined;
    if (summary !== undefined && (alert || !summary || summary.length > POST_LIMITS.summary))
      return fail(`Only an update has a summary, a line of up to ${POST_LIMITS.summary} characters.`);
    return this.connection.post({
      ...(typeof topic === "string" && !alert ? { topic } : {}),
      alert,
      text,
      link: typeof value.link === "string" ? value.link : null,
      mention: mention as string[],
      chat: typeof value.chat === "string" ? value.chat : null,
      ...(summary ? { summary } : {}),
    });
  }
  /** Whether this is a question's view (docs/agent.md#asking-you), which its object says. */
  get asked(): boolean {
    return this.connection.data?.asked === true;
  }
  /** Whether this is a view posted in a message, opened from it (docs/views.md#15-views-in-messages), which its object says. */
  get posted(): boolean {
    return this.connection.data?.posted === true;
  }
  /**
   * `coast.respond`: the viewer's answer to the question an agent asked with this view (docs/agent.md#asking-you), or
   * their response to a view posted in a message, which the chat keeps with the message (docs/views.md#15-views-in-messages):
   * any JSON up to its size, which the view's object keeps, and the label of what they chose, plain text that shows as
   * their answer (`labelOf`). Only those two, which the view's object says they are, respond; any other refuses, saying so.
   */
  respond(value: unknown, label?: unknown): Promise<unknown> {
    if (!this.asked && !this.posted)
      return Promise.reject(
        Object.assign(
          new Error(
            "coast.respond answers a question an agent asked with this view, or a view posted in a message, and this view is neither.",
          ),
          { code: "invalid" },
        ),
      );
    const json = JSON.stringify(value ?? null);
    if (value === undefined || value === null || new TextEncoder().encode(json).byteLength > QUESTION_LIMITS.valueBytes)
      return Promise.reject(
        Object.assign(
          new Error(`coast.respond takes a value: any JSON up to ${QUESTION_LIMITS.valueBytes / 1024} KB.`),
          {
            code: "invalid",
          },
        ),
      );
    const named = labelOf(label);
    if (named === null) return Promise.reject(Object.assign(new Error(LABEL_PROBLEM), { code: "invalid" }));
    return this.connection.respond(JSON.parse(json), named);
  }
  /**
   * One of the view's declared actions (docs/views.md#14-queries-and-actions), run on the server as the viewer. In the
   * home, `chat` names the chat it is about, when the home reads several.
   */
  call(name: string, params: Record<string, unknown>, options: { chat?: string } = {}): Promise<unknown> {
    if (!this.manifest().actions[name])
      return Promise.reject(Object.assign(new Error(`This view has no action named ${name}.`), { code: "action" }));
    return this.connection.call(name, params, options.chat ?? null);
  }
  /** This device's last answer to one of the view's queries with these parameters, when it keeps them. */
  cached(name: string, params: Record<string, unknown>): { value: unknown; at: string } | null {
    const spec = this.manifest().queries[name];
    return spec && spec.cache !== false ? (this.connection.cached?.(name, params) ?? null) : null;
  }

  private note(detail: DataChangeDetail) {
    if (detail.uses) {
      this.dirtyUses = true;
      return;
    }
    if (!detail.part) {
      this.dirtyAll = true;
      return;
    }
    const known = this.dirty.get(detail.part);
    if (known === "whole") return;
    if (!detail.records) {
      this.dirty.set(detail.part, "whole");
      return;
    }
    const byCollection = known ?? new Map<string, Set<string>>();
    for (const { collection, id } of detail.records) {
      const ids = byCollection.get(collection) ?? new Set<string>();
      byCollection.set(collection, ids.add(id));
    }
    this.dirty.set(detail.part, byCollection);
  }
  /** The page's frame was handed all of `snapshot`, as it boots or connects: it holds that, with nothing left to tell. */
  handed(snapshot: FrameData) {
    this.told.clear();
    for (const [part, rows] of Object.entries(snapshot.parts))
      for (const [collection, list] of Object.entries(rows))
        this.told.set(
          `${part}\n${collection}`,
          list.map((row) => row.id),
        );
    this.toldReady = snapshot.ready;
    this.dirty.clear();
    this.dirtyAll = this.dirtyUses = false;
  }
  /**
   * What the page's frame is missing, as increments (frame/protocol.ts): the records that changed, by id, and the ids'
   * order only where the frame's would come out otherwise; a partition that changed whole, or one the frame holds no
   * longer, whole, by collection. Null when nothing it reads changed. What it returns, the frame is taken to hold.
   */
  changes(): DataChange | null {
    const held = (this.connection.data?.parts ?? []).filter((key) => this.connection.parts.has(key));
    const rowsOf = (part: string, collection: string) =>
      held.includes(part) ? this.copy.rowsIn(part, collection) : [];
    const collections: NonNullable<DataChange["collections"]> = [];
    const whole = new Set([...this.dirty].flatMap(([part, entry]) => (entry === "whole" ? [part] : [])));
    if (this.dirtyAll)
      for (const part of [...held, ...[...this.told.keys()].map((key) => key.split("\n")[0]!)]) whole.add(part);
    for (const part of whole) {
      const told = [...this.told.keys()]
        .filter((key) => key.startsWith(`${part}\n`))
        .map((key) => key.slice(part.length + 1));
      const names = new Set([...(held.includes(part) ? this.connection.collections(part) : []), ...told]);
      for (const collection of names) {
        const rows = rowsOf(part, collection);
        collections.push({ part, collection, rows });
        this.remember(
          part,
          collection,
          rows.map((row) => row.id),
        );
      }
    }
    for (const [part, entry] of this.dirty) {
      if (entry === "whole" || whole.has(part)) continue;
      for (const [collection, ids] of entry) {
        const rows = rowsOf(part, collection);
        const upsert = rows.filter((row) => ids.has(row.id));
        const present = new Set(rows.map((row) => row.id));
        const remove = [...ids].filter((id) => !present.has(id));
        if (!upsert.length && !remove.length) continue;
        // The frame keeps a record where it is and puts a new one last; the order goes along when that isn't this one.
        const before = this.told.get(`${part}\n${collection}`) ?? [];
        const had = new Set(before);
        const gone = new Set(remove);
        const expected = before
          .filter((id) => !gone.has(id))
          .concat(upsert.flatMap((row) => (had.has(row.id) ? [] : [row.id])));
        const order = rows.map((row) => row.id);
        const kept = expected.length === order.length && expected.every((id, index) => id === order[index]);
        collections.push({ part, collection, upsert, remove, ...(kept ? {} : { order }) });
        this.remember(part, collection, order);
      }
    }
    const uses = this.dirtyUses ? this.usesNow() : null;
    const ready = this.ready();
    this.dirty.clear();
    this.dirtyAll = this.dirtyUses = false;
    if (!collections.length && !uses && ready === this.toldReady) return null;
    this.toldReady = ready;
    return {
      version: this.version,
      ready,
      ...(collections.length ? { collections } : {}),
      ...(uses ? { uses } : {}),
    };
  }
  private remember(part: string, collection: string, ids: string[]) {
    if (ids.length) this.told.set(`${part}\n${collection}`, ids);
    else this.told.delete(`${part}\n${collection}`);
  }
  /** The records of the views this one reads through `uses`, as the frame keeps them. */
  private usesNow(): FrameData["uses"] {
    return Object.fromEntries(
      this.manifest().uses.flatMap((name) => {
        const used = this.used(name);
        return used ? [[name, used]] : [];
      }),
    );
  }

  /** Everything a page's frame may read, as its copy keeps it (frame/store.ts): what `find`, `can`, and `locate` read here. */
  snapshot(): FrameData {
    const info = this.connection.data,
      manifest = this.manifest();
    const parts: FrameData["parts"] = {};
    for (const key of info?.parts ?? []) {
      if (!this.connection.parts.has(key) || !parsePartition(key)) continue;
      parts[key] = Object.fromEntries(
        this.connection.collections(key).map((collection) => [collection, this.copy.rowsIn(key, collection)]),
      );
    }
    const rules = Object.fromEntries(
      Object.entries(manifest.collections).map(([name, spec]) => [
        name,
        { scope: spec.scope, write: spec.write, ...(spec.key ? { key: spec.key } : {}) },
      ]),
    );
    const uses = this.usesNow();
    return {
      version: this.version,
      ready: this.ready(),
      chat: info?.chat ?? null,
      chats: this.place()?.chats ?? [],
      writer: info?.writer ?? null,
      parts,
      rules,
      sections: manifest.sections,
      queries: manifest.queries,
      actions: manifest.actions,
      uses,
      links: info?.links ?? null,
    };
  }
  /** This device's kept answers to the view's queries, by `name:params`, and the queries that may be kept. */
  answers(): { answers: Record<string, { value: unknown; at: string }>; cacheable: string[] } {
    const queries = this.manifest().queries;
    const cacheable = Object.entries(queries)
      .filter(([, spec]) => spec.cache !== false)
      .map(([name]) => name);
    const kept = this.connection.answers?.() ?? {};
    return {
      answers: Object.fromEntries(
        Object.entries(kept).filter(([key]) => cacheable.includes(key.slice(0, key.indexOf(":")))),
      ),
      cacheable,
    };
  }

  /** Another view's collection that the manifest names in `uses`, as the view's object sent it. */
  private used(collection: string): UsedCollection | null {
    const entry = this.manifest().uses.includes(collection) ? this.connection.data?.uses?.[collection] : null;
    return entry && Array.isArray(entry.records) ? entry : null;
  }

  /**
   * The record a link names, by id, and the section that shows it: this view's own first, then another view's from
   * `uses`, with that view's link, where the record opens.
   */
  locate(id: string): { collection: string; section: string | null; link?: string | null } | null {
    return this.copy.locate(id, this.manifest().uses);
  }

  /**
   * The chat's members as `coast.people` lists them, with whether they have the view open. A link view has no members,
   * so whoever has it open stands in for them.
   */
  people(here: Set<string> = new Set(), visitors: { id: string; name: string }[] = []): Person[] {
    const members: ViewMember[] = this.connection.data?.members ?? [];
    if (!members.length) {
      const seen = new Set<string>();
      return visitors
        .filter((visitor) => !seen.has(visitor.id) && !!seen.add(visitor.id))
        .map((visitor) => person(visitor.id, visitor.name, "member", true));
    }
    return members.map((member) =>
      person(
        member.id,
        member.name,
        member.role,
        here.has(member.id) || member.id === this.connection.data?.writer.id,
        member.icon,
      ),
    );
  }
  /** The viewer as `coast.me`: as the chat lists them, or by the name this device knows them by. */
  me(fallback: { id: string; name: string }): Person {
    const info = this.connection.data,
      id = info?.writer.id ?? fallback.id;
    return (
      this.people().find((listed) => listed.id === id) ?? person(id, fallback.name, info?.writer.role ?? null, true)
    );
  }
}

const person = (id: string, name: string, role: string | null, here: boolean, icon?: OwnIcon | null): Person => ({
  id,
  name,
  short: name.split(/\s+/)[0] || name,
  color: colorOf(name),
  kind: "person",
  role,
  status: null,
  here,
  icon: icon ?? null,
});

/**
 * A view's data kept in this page alone, for previews where nothing reaches a server: each write is staged as the
 * server stages it (shared/views/stage.ts), so records get their numbers and stamps and a rule refuses what it would.
 */
export class LocalData extends EventTarget implements DataConnection {
  readonly parts = new Map<string, PartCopy>();
  /** What the page posted, kept here, since no chat hears a local copy. */
  readonly posted: PagePost[] = [];
  /** What a question's page answered with, and the label it gave, kept here, since no question hears a local copy. */
  readonly responded: { value: unknown; label?: string }[] = [];
  data: DataInfo;
  readonly readyParts: Set<string>;
  private counters = new Map<string, number>();
  private log = new Map<string, HistoryEntry[]>();
  constructor(
    info: Omit<DataInfo, "parts">,
    private readonly manifest: () => Manifest,
  ) {
    super();
    const parts = [
      // Without a chat, as in a link view, the chat is `link`, as the view's object names it.
      ...(info.chats ?? [info.chat ?? "link"]).map((chat) => `chat:${chat}`),
      "view",
      `personal:${info.writer.id}`,
    ];
    this.data = { ...info, parts };
    for (const part of parts) this.parts.set(part, { revision: 0, rows: {} });
    this.readyParts = new Set(parts);
  }
  rows(part: string, collection: string): Rec[] {
    return Object.values(this.parts.get(part)?.rows[collection] ?? {});
  }
  collections(part: string): string[] {
    return Object.keys(this.parts.get(part)?.rows ?? {});
  }
  /** A write staged as the server stages it, by whoever `seeded` names, or the viewer. */
  private stage(part: string, op: Op, seeded?: SeedWrite): DataAck {
    const copy = this.parts.get(part);
    if (!copy) throw new Error("That partition is not open.");
    const info = this.data;
    const writer: Writer =
      seeded?.writer ?? placeWriter({ chats: info.chats ?? (info.chat ? [info.chat] : []), writer: info.writer });
    const number = (collection: string) => {
      const last = this.counters.get(collection) ?? 0,
        n = seeded?.n ?? last + 1;
      this.counters.set(collection, Math.max(last, n));
      return n;
    };
    const staged = stageOps(
      {
        partition: parsePartition(part)!,
        manifest: this.manifest(),
        writer,
        at: seeded?.at ?? new Date().toISOString(),
        number,
        read: (collection, id) => copy.rows[collection]?.[id] ?? null,
      },
      [op],
    );
    if (staged.accepted.length) copy.revision++;
    for (const change of staged.accepted) {
      copy.rows[change.collection] ??= {};
      const rows = copy.rows[change.collection]!;
      if (change.after) rows[change.id] = change.after;
      else delete rows[change.id];
    }
    for (const entry of staged.history) {
      const key = `${part}/${entry.collection}/${entry.id}`;
      this.log.set(key, [...(this.log.get(key) ?? []), entry]);
    }
    this.dispatchEvent(
      new CustomEvent("data", {
        detail: {
          part,
          records: staged.accepted.map((change) => ({ collection: change.collection, id: change.id })),
        } satisfies DataChangeDetail,
      }),
    );
    return {
      revision: copy.revision,
      refused: staged.refused.map((change) => ({ collection: change.collection, id: change.id })),
    };
  }
  async writeData(part: string, op: Op): Promise<DataAck> {
    return this.stage(part, op);
  }
  /** Other views' records for `uses`, as a preview takes them from those views' templates' seeds. */
  setUses(uses: DataInfo["uses"]) {
    this.data = { ...this.data, uses };
    this.dispatchEvent(new CustomEvent("data", { detail: { uses: true } }));
  }
  /** A seed's write (`shared/views/seed.ts`), as its author and at its time, for a preview's sample data. */
  seed(write: SeedWrite): DataAck {
    return this.parts.has(write.part) ? this.stage(write.part, write.op, write) : { revision: 0, refused: [] };
  }
  async history(part: string, collection: string, record: string): Promise<unknown[]> {
    return this.log.get(`${part}/${collection}/${record}`) ?? [];
  }
  /** A preview has no chat, and so no connectors to query. */
  async query(): Promise<unknown> {
    throw Object.assign(new Error("Queries run in a chat; a preview has none."), { code: "unavailable" });
  }
  /** Nor a server to run its actions. */
  async post(post: PagePost): Promise<unknown> {
    this.posted.push(post);
    return null;
  }
  async respond(value: unknown, label?: string): Promise<unknown> {
    this.responded.push({ value, ...(label === undefined ? {} : { label }) });
    return null;
  }
  async call(): Promise<unknown> {
    throw Object.assign(new Error("Actions run in a chat; a preview has none."), { code: "unavailable" });
  }
}
